import React, { useCallback, useContext, useEffect, useState } from "react";
import { Button } from "@panda0/panda0-ui";
import { ArrowLeft } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { toast } from "sonner";

import { JobFormData } from "@/lib/types";
import { ScreeningQuestionSchema } from "@/lib/zod/schemas/job";
import FormPreviewBrowser from "./customize/form-preview-browser";
import SectionEditor from "./customize/section-editor";
import SectionGrid from "./customize/section-grid";
import { FormData } from "./customize/types";
import { JobModalContext } from "./types";

function JobCustomize() {
  const { savedJobId, jobData, stateMachine } = useContext(JobModalContext);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<number>(Date.now());

  // Use the existing form context from the parent
  const methods = useFormContext<JobFormData>();

  // The parent form keeps everything under pandaQuizCustomization
  // so we need to use setValue, watch etc with that path prefix
  const {
    setValue,
    watch,
    formState: { isDirty, dirtyFields },
    trigger,
    reset,
  } = methods;

  // Get values with the correct path
  const watchScreeningQuestions = watch("screeningQuestions");
  const watchProctoringSettings = watch("proctoringSettings");
  const watchSubmissionSettings = watch("submissionSettings");
  const watchVideoResume = watch("videoResume");
  const watchJobDetails = watch("jobDetails");
  const watchFormDetails = watch("formDetails");
  const watchReferralRewards = watch("referralRewards");
  const watchQuizSettings = watch("quizSettings");

  // Initialize default values
  useEffect(() => {
    // Only set default values if the sections don't exist yet
    if (!watchProctoringSettings) {
      setValue(
        "proctoringSettings",
        {
          PROCTORING_ENABLED: false,
          PROCTORING_MULTIFACE_DETECTION: true,
          PROCTORING_VOICE_MONITORING: true,
          PROCTORING_EYE_TRACKING: true,
          PROCTORING_LIVENESS_ENABLED: true,
          PROCTORING_PERIODIC_SNAPSHOTS: true,
          PROCTORING_RED_FLAG_SNAPSHOTS: true,
          PROCTORING_BLOCK_SCREEN_SHARING: true,
          PROCTORING_OBJECT_DETECTION: true,
          PROCTORING_BROWSER_FOCUS: true,
        },
        { shouldDirty: false }
      );
    }

    if (!watchSubmissionSettings) {
      setValue(
        "submissionSettings",
        {
          QUIZSUBMISSION_RESUME_UPLOAD: false,
          QUIZSUBMISSION_SHARE_RESULTS: "NEVER",
          QUIZSUBMISSION_EMAIL_CONFIRMATION: false,
          QUIZSUBMISSION_NOTIFY_HIRING_TEAM: false,
          QUIZ_GAME_PLAY: false,
        },
        { shouldDirty: false }
      );
    }

    if (!watchVideoResume) {
      setValue(
        "videoResume",
        {
          QUIZSUBMISSION_VIDEO_RESUME: false,
          QUIZSUBMISSION_VIDEO_RESUME_INSTRUCTIONS: "",
          QUIZSUBMISSION_VIDEO_RESUME_MAXDURATION: "",
        },
        { shouldDirty: false }
      );
    }

    if (!watchJobDetails) {
      setValue(
        "jobDetails",
        {
          JOBINFORMATION_SALARYCURRENCY: "INR",
          JOBINFORMATION_SALARYMIN: "",
          JOBINFORMATION_SALARYMAX: "",
          JOBINFORMATION_WORKTYPE: "",
          JOBINFORMATION_WORKMODE: "",
          JOBINFORMATION_WORKLOCATION: "",
          JOBINFORMATION_SHORTDESCRIPTION: "",
          JOBINFORMATION_PERKSBENEFITS: "",
          JOBINFORMATION_OGDESCRIPTION: "",
        },
        { shouldDirty: false }
      );
    }

    if (!watchFormDetails) {
      setValue(
        "formDetails",
        {
          ISREQUIRED_FORMSETTINGS_RESUMEREQUIRED: false,
          ISREQUIRED_FORMSETTINGS_CONTACTNUMBEREQUIRED: false,
          ISREQUIRED_FORMSETTINGS_LOCATIONREQUIRED: false,
          ISREQUIRED_FORMSETTINGS_LINKEDINREQUIRED: false,
          ISREQUIRED_FORMSETTINGS_GITHUBREQUIRED: false,
          ISREQUIRED_FORMSETTINGS_EDUCATIONREQUIRED: false,
          ISREQUIRED_FORMSETTINGS_CTCREQUIRED: false,
          ISREQUIRED_FORMSETTINGS_CANDIDATECONSENTREQUIRED: false,
          ISREQUIRED_FORMSETTINGS_CONSENTTEXT: false,
          FORMSETTINGS_RESUMEREQUIRED: false,
          FORMSETTINGS_CONTACTNUMBEREQUIRED: false,
          FORMSETTINGS_LOCATIONREQUIRED: false,
          FORMSETTINGS_LINKEDINREQUIRED: false,
          FORMSETTINGS_GITHUBREQUIRED: false,
          FORMSETTINGS_EDUCATIONREQUIRED: false,
          FORMSETTINGS_CTCREQUIRED: false,
          FORMSETTINGS_CANDIDATECONSENTREQUIRED: false,
          FORMSETTINGS_CONSENTTEXT: "",
        },
        { shouldDirty: false }
      );
    }

    if (!watchReferralRewards) {
      setValue(
        "referralRewards",
        {
          REFERRAL_REWARDTYPE: "CASH",
          REFERRAL_DETAILS: "",
          REFERRAL_CURRENCY: "USD",
          REFERRAL_AMOUNT: "",
          REFERRAL_MUSTBEHIRED: false,
          REFERRAL_MUSTCOMPLETEPROBATION: false,
        },
        { shouldDirty: false }
      );
    }

    if (!watchQuizSettings) {
      setValue(
        "quizSettings",
        {
          QUIZ_SHARERESULTS: "NEVER",
          QUIZ_TIMELIMIT: "30",
          QUIZ_PASSINGSCORE: "70",
          QUIZ_RANDOMIZEQUESTIONS: false,
          QUIZ_ALLOWMULTIPLEATTEMPTS: false,
          QUIZ_SHOWCORRECTANSWERS: false,
        },
        { shouldDirty: false }
      );
    }

    if (!watchScreeningQuestions) {
      setValue("screeningQuestions", [], { shouldDirty: false });
    }
  }, []);

  // Update form values when job data changes
  useEffect(() => {
    if (jobData) {
      // Create an object to hold all form values
      const formValues: Partial<JobFormData> = {};

      if ((jobData?.screeningQuestions?.length ?? 0) > 0) {
        formValues.screeningQuestions = jobData.screeningQuestions!;
      }

      if (jobData.proctoringSettings) {
        formValues.proctoringSettings = jobData.proctoringSettings;
      }

      if (jobData.submissionSettings) {
        formValues.submissionSettings = jobData.submissionSettings;
      }

      if (jobData.jobDetails) {
        formValues.jobDetails = jobData.jobDetails;
      }

      if (jobData.formDetails) {
        formValues.formDetails = jobData.formDetails;
      }

      if (jobData.referralRewards) {
        formValues.referralRewards = jobData.referralRewards;
      }

      if (jobData.videoResume) {
        formValues.videoResume = jobData.videoResume;
      }

      if (jobData.quizSettings) {
        formValues.quizSettings = jobData.quizSettings;
      }

      // Reset the form with the new values to clear dirty state
      reset(formValues, { keepDirtyValues: false });
      setLastSaved(Date.now());
    }
  }, [jobData, reset]);

  const setCustomizationValue = (path: string, value: any, options?: any) => {
    //@ts-ignore
    setValue(`${path}`, value, { ...options, shouldDirty: true });
  };

  // Save form data
  const handleSave = useCallback(
    async (skipToast = false) => {
      // If nothing has changed, don't save
      if (!isDirty) {
        if (!skipToast) {
          toast.info("No changes to save");
        }
        return true;
      }

      stateMachine.trackEvent("job_customize_save", {
        changed_sections: Object.keys(dirtyFields),
        proctoring_enabled: Boolean(
          watch("proctoringSettings.PROCTORING_ENABLED")
        ),
        screening_questions_count: (watch("screeningQuestions") || []).length,
        has_video_resume: Boolean(
          watch("videoResume.QUIZSUBMISSION_VIDEO_RESUME")
        ),
      });

      setIsSaving(true);
      try {
        await stateMachine.saveStep("customize", true);

        setLastSaved(Date.now());

        if (!skipToast) {
          toast.success("Job customization saved successfully");
        }
        return true;
      } catch (error) {
        console.error("Error saving job customization:", error);
        if (!skipToast) {
          toast.error("Failed to save job customization");
        }
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [isDirty, stateMachine, dirtyFields, watch]
  );

  // Handle section change without auto-saving
  const handleSectionChange = useCallback(
    (section: string) => {
      stateMachine.trackEvent("job_customize_section_change", {
        section_name: section,
        previous_section: activeSection,
      });

      setActiveSection(section);
    },
    [activeSection, stateMachine]
  );

  // Define section order for navigation
  const sectionOrder = [
    "jobDetails",
    "formDetails",
    "screeningQuestions",
    "videoResume",
    "proctoringSettings",
    "referralRewards",
    "quizSettings",
  ];

  // Handle navigation to next section with optional save
  const handleNextSection = useCallback(async () => {
    if (!activeSection) return;

    stateMachine.trackEvent("job_customize_next_section", {
      current_section: activeSection,
      has_changes: isDirty,
    });

    // Optionally save first if there are changes
    if (isDirty) {
      const saveSuccessful = await handleSave(true); // Skip toast notification
      if (!saveSuccessful) {
        // If save failed, don't navigate
        return;
      }
    }

    const currentIndex = sectionOrder.indexOf(activeSection);
    if (currentIndex < sectionOrder.length - 1) {
      setActiveSection(sectionOrder[currentIndex + 1]);
    } else {
      // If we're at the last section, go back to grid view
      setActiveSection(null);
    }
  }, [activeSection, isDirty, handleSave, sectionOrder, stateMachine]);

  // Create a mock FormData object to pass down to children
  const formData: FormData = {
    screeningQuestions: watchScreeningQuestions || jobData?.screeningQuestions,
    proctoringSettings: watchProctoringSettings || jobData?.proctoringSettings,
    submissionSettings: watchSubmissionSettings || jobData?.submissionSettings,
    jobDetails: watchJobDetails || jobData?.jobDetails,
    formDetails: watchFormDetails || jobData?.formDetails,
    referralRewards: watchReferralRewards || jobData?.referralRewards,
    quizSettings: watchQuizSettings || jobData?.quizSettings,
    videoResume: watchVideoResume || jobData?.videoResume,
  };

  // Function to manually prompt save before leaving current section
  const handleBackWithSave = useCallback(async () => {
    if (!activeSection) return;

    await handleSave(true);

    setActiveSection(null);
  }, [activeSection, isDirty, handleSave, jobData]);

  const handlePrevWithSave = useCallback(async () => {
    if (!activeSection) return;

    if (isDirty) {
      const shouldSave = confirm(
        "Do you want to save your changes before going back?"
      );
      if (shouldSave) {
        await handleSave(true);
      } else {
        switch (activeSection) {
          case "screeningQuestions":
            setValue("screeningQuestions", jobData?.screeningQuestions || [], {
              shouldDirty: false,
            });
            break;
          case "proctoringSettings":
            setValue(
              "proctoringSettings",
              jobData?.proctoringSettings || {
                PROCTORING_ENABLED: false,
                PROCTORING_MULTIFACE_DETECTION: true,
                PROCTORING_VOICE_MONITORING: true,
                PROCTORING_EYE_TRACKING: true,
                PROCTORING_LIVENESS_ENABLED: true,
                PROCTORING_PERIODIC_SNAPSHOTS: true,
                PROCTORING_RED_FLAG_SNAPSHOTS: true,
                PROCTORING_BLOCK_SCREEN_SHARING: true,
                PROCTORING_OBJECT_DETECTION: true,
                PROCTORING_BROWSER_FOCUS: true,
              },
              { shouldDirty: false }
            );
            break;
          case "submissionSettings":
            setValue(
              "submissionSettings",
              jobData?.submissionSettings || {
                QUIZSUBMISSION_RESUME_UPLOAD: false,
                QUIZSUBMISSION_SHARE_RESULTS: "NEVER",
                QUIZSUBMISSION_EMAIL_CONFIRMATION: false,
                QUIZSUBMISSION_NOTIFY_HIRING_TEAM: false,
              },
              { shouldDirty: false }
            );
            break;
          case "videoResume":
            setValue(
              "videoResume",
              jobData?.videoResume || {
                QUIZSUBMISSION_VIDEO_RESUME: false,
                QUIZSUBMISSION_VIDEO_RESUME_INSTRUCTIONS: "",
                QUIZSUBMISSION_VIDEO_RESUME_MAXDURATION: "",
              },
              { shouldDirty: false }
            );
            break;
          case "jobDetails":
            setValue(
              "jobDetails",
              jobData?.jobDetails || {
                JOBINFORMATION_SALARYCURRENCY: "INR",
                JOBINFORMATION_SALARYMIN: "",
                JOBINFORMATION_SALARYMAX: "",
                JOBINFORMATION_WORKTYPE: "",
                JOBINFORMATION_WORKMODE: "",
                JOBINFORMATION_WORKLOCATION: "",
                JOBINFORMATION_SHORTDESCRIPTION: "",
                JOBINFORMATION_PERKSBENEFITS: "",
                JOBINFORMATION_OGDESCRIPTION: "",
              },
              { shouldDirty: false }
            );
            break;
          case "formDetails":
            setValue(
              "formDetails",
              jobData?.formDetails || {
                ISREQUIRED_FORMSETTINGS_RESUMEREQUIRED: false,
                ISREQUIRED_FORMSETTINGS_CONTACTNUMBEREQUIRED: false,
                ISREQUIRED_FORMSETTINGS_LOCATIONREQUIRED: false,
                ISREQUIRED_FORMSETTINGS_LINKEDINREQUIRED: false,
                ISREQUIRED_FORMSETTINGS_GITHUBREQUIRED: false,
                ISREQUIRED_FORMSETTINGS_EDUCATIONREQUIRED: false,
                ISREQUIRED_FORMSETTINGS_CTCREQUIRED: false,
                ISREQUIRED_FORMSETTINGS_CANDIDATECONSENTREQUIRED: false,
                ISREQUIRED_FORMSETTINGS_CONSENTTEXT: false,
                FORMSETTINGS_RESUMEREQUIRED: false,
                FORMSETTINGS_CONTACTNUMBEREQUIRED: false,
                FORMSETTINGS_LOCATIONREQUIRED: false,
                FORMSETTINGS_LINKEDINREQUIRED: false,
                FORMSETTINGS_GITHUBREQUIRED: false,
                FORMSETTINGS_EDUCATIONREQUIRED: false,
                FORMSETTINGS_CTCREQUIRED: false,
                FORMSETTINGS_CANDIDATECONSENTREQUIRED: false,
                FORMSETTINGS_CONSENTTEXT: "",
              },
              { shouldDirty: false }
            );
            break;
          case "referralRewards":
            setValue(
              "referralRewards",
              jobData?.referralRewards || {
                REFERRAL_REWARDTYPE: "CASH",
                REFERRAL_DETAILS: "",
                REFERRAL_CURRENCY: "USD",
                REFERRAL_AMOUNT: "",
                REFERRAL_MUSTBEHIRED: false,
                REFERRAL_MUSTCOMPLETEPROBATION: false,
              },
              { shouldDirty: false }
            );
            break;
          case "quizSettings":
            setValue(
              "quizSettings",
              jobData?.quizSettings || {
                QUIZ_SHARERESULTS: "NEVER",
                QUIZ_TIMELIMIT: "30",
                QUIZ_PASSINGSCORE: "70",
                QUIZ_RANDOMIZEQUESTIONS: false,
                QUIZ_ALLOWMULTIPLEATTEMPTS: false,
                QUIZ_SHOWCORRECTANSWERS: false,
              },
              { shouldDirty: false }
            );
            break;
        }
      }
    }

    const currentIndex = sectionOrder.indexOf(activeSection);
    if (currentIndex > 0) {
      setActiveSection(sectionOrder[currentIndex - 1]);
    } else {
      setActiveSection(null);
    }
  }, [activeSection, isDirty, handleSave, jobData, sectionOrder]);

  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-10rem)]">
      {/* Header with title and save button */}
      <header className="flex justify-between items-center px-4 py-3 border-b">
        {activeSection ? (
          <button
            onClick={handleBackWithSave}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            type="button" // Explicitly set as button type to prevent form submission
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </button>
        ) : (
          <h1 className="text-lg font-bold">Job Customization</h1>
        )}
        <div className="flex items-center gap-2">
          {isDirty && (
            <span className="text-xs text-gray-500">Unsaved changes</span>
          )}
          <Button
            onClick={() => handleSave(false)}
            disabled={isSaving || !isDirty}
            text={isSaving ? "Saving..." : "Save"}
            className="w-fit text-sm h-8 px-3"
            size="sm"
            type="button" // Explicitly set as button type
          />
        </div>
      </header>

      {/* Main content area with two panels */}
      <div className="flex flex-col md:flex-row flex-1 h-[calc(100%-3rem)]">
        {/* Left panel - Customization options */}
        <div className="w-full md:w-1/3 p-3 h-full border-r">
          {activeSection ? (
            <div className="h-full overflow-auto pr-1">
              <CustomizationContext.Provider
                value={{ formData, setValue: setCustomizationValue, watch }}
              >
                <SectionEditor
                  section={activeSection}
                  onBack={handlePrevWithSave}
                  onNext={handleNextSection}
                />
              </CustomizationContext.Provider>
            </div>
          ) : (
            <CustomizationContext.Provider
              value={{ formData, setValue: setCustomizationValue, watch }}
            >
              <SectionGrid onSelect={handleSectionChange} />
            </CustomizationContext.Provider>
          )}
        </div>

        {/* Right panel - Preview */}
        <div className="w-full md:w-2/3 bg-gray-50 p-3 h-full overflow-auto">
          <div className="lg:sticky lg:top-24">
            <CustomizationContext.Provider
              value={{ formData, setValue: setCustomizationValue, watch }}
            >
              <FormPreviewBrowser domain={`hirepanda.com`} />
            </CustomizationContext.Provider>
          </div>
        </div>
      </div>
    </div>
  );
}

// Create a context to pass down the form data and wrapped setValue function
export const CustomizationContext = React.createContext<{
  formData: FormData;
  setValue: (path: string, value: any, options?: any) => void;
  watch: (path?: string) => any;
}>({
  formData: {} as FormData,
  setValue: () => {},
  watch: () => {},
});

export default React.memo(JobCustomize);
